import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Scales, CheckCircle, LockKey } from "@phosphor-icons/react";

function ConsentGate({ onSigned }) {
  const [name, setName] = useState("");
  const [checked, setChecked] = useState(false);
  const sign = useMutation({
    mutationFn: () => api.post("/portal/consent", { typed_name: name, consent: checked }),
    onSuccess: () => { toast.success("Consent recorded — thank you"); onSigned(); },
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4" data-testid="consent-gate">
      <p className="text-xs font-medium text-zinc-900 mb-1">IRC § 7216 consent required</p>
      <p className="text-[11px] text-zinc-600 mb-3">Federal law requires your explicit consent before our secure AI may analyze your tax information. Your data is used only to prepare your returns, is never used to train public models, and every access is audit-logged.</p>
      <Input data-testid="consent-name-input" placeholder="Type your full legal name" value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-xs mb-2" />
      <label className="flex items-start gap-2 text-[10px] text-zinc-600 cursor-pointer mb-2">
        <input data-testid="consent-checkbox" type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span>I consent under IRC § 7216 to the use of my tax return information for AI-assisted analysis and preparation by my firm. This typed name is my electronic signature.</span>
      </label>
      <Button data-testid="consent-sign-btn" size="sm" className="w-full h-8 text-xs" disabled={!name.trim() || !checked || sign.isPending} onClick={() => sign.mutate()}>Sign consent</Button>
    </div>
  );
}

export function ComplianceInterview({ checklists }) {
  const qc = useQueryClient();
  const [engId, setEngId] = useState(null);
  const [details, setDetails] = useState({});
  const active = engId || checklists?.[0]?.engagement_id;

  const { data: session, refetch } = useQuery({
    queryKey: ["compliance", active],
    queryFn: () => api.get(`/portal/compliance/${active}`).then((r) => r.data),
    enabled: !!active,
  });

  const answer = useMutation({
    mutationFn: ({ q, a }) => api.post("/portal/compliance/answer", { session_id: session.id, question_id: q.id, answer: a, details: details[q.id] || null }),
    onSuccess: ({ data }) => {
      refetch();
      if (data.added_document) toast.success(`Added to your checklist: ${data.added_document.label}`);
      if (data.completed) toast.success("Due diligence complete — recorded for your CPA's file");
      qc.invalidateQueries({ queryKey: ["portal"] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  if (!checklists?.length) return null;

  const qs = session?.questions || [];
  const byId = Object.fromEntries(qs.map((q) => [q.id, q]));
  const visible = qs.filter((q) => !q.depends_on || byId[q.depends_on]?.answer === "yes");
  const done = visible.length > 0 && visible.every((q) => q.answer);
  const locked = session?.locked;

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="compliance-interview">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><Scales size={15} /> Required compliance questions</h3>
        {done && !locked && <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-2 py-0.5 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> COMPLETE</span>}
        {locked && <span className="text-[10px] bg-zinc-100 text-zinc-500 border border-zinc-200 rounded-sm px-2 py-0.5 flex items-center gap-1"><LockKey size={11} /> LOCKED — FILED</span>}
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">IRS-required due diligence — your answers become part of your preparer's audit-ready file.</p>
      {checklists.length > 1 && (
        <select data-testid="compliance-engagement-select" className="mb-3 w-full text-xs border border-zinc-200 rounded-md px-2 py-1.5 bg-white" value={active} onChange={(e) => setEngId(e.target.value)}>
          {checklists.map((c) => <option key={c.engagement_id} value={c.engagement_id}>{c.service_type} · {c.tax_year}</option>)}
        </select>
      )}
      {session?.consent_required ? (
        <ConsentGate onSigned={() => refetch()} />
      ) : (
        <div className="space-y-2.5">
          <p className="text-[10px] text-zinc-400">{visible.filter((q) => q.answer).length} of {visible.length} answered</p>
          {visible.map((q) => (
            <div key={q.id} data-testid={`cq-${q.id}`} className="rounded-md border border-zinc-100 p-3">
              <p className="text-[9px] uppercase tracking-wide text-zinc-400 mb-0.5">{q.irc}</p>
              <p className="text-xs text-zinc-800">{q.text}</p>
              {q.answer ? (
                <p className="text-[10px] mt-1.5 text-zinc-500">Answered: <span className={`font-medium uppercase ${q.answer === "yes" ? "text-indigo-700" : "text-zinc-700"}`}>{q.answer}</span>{q.details && ` — "${q.details}"`}</p>
              ) : locked ? (
                <p className="text-[10px] mt-1.5 text-zinc-400 italic">Unanswered (locked)</p>
              ) : (
                <div className="mt-2">
                  <Input data-testid={`cq-details-${q.id}`} placeholder="Optional details…" value={details[q.id] || ""} onChange={(e) => setDetails({ ...details, [q.id]: e.target.value })} className="h-7 text-xs mb-1.5" />
                  <div className="flex gap-1.5">
                    {["yes", "no", "na"].map((a) => (
                      <button key={a} data-testid={`cq-${q.id}-${a}`} disabled={answer.isPending} onClick={() => answer.mutate({ q, a })}
                        className="flex-1 text-[11px] uppercase border border-zinc-200 rounded-md py-1 hover:bg-zinc-900 hover:text-white transition-colors duration-150">
                        {a === "na" ? "N/A" : a}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
